import {
  Autocomplete,
  Checkbox,
  Grid,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { DateRangePicker } from "@mui/x-date-pickers-pro";
import { useEffect, useRef, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../../app/hooks";
import CommonTextField from "../../../../common/components/functional/commonTextField/CommonTextField";
import NumberRange from "../../../../common/components/functional/fields/numberRange/NumberRange";
import { SearchFilter } from "../../../../common/components/functional/FilterSearchField/FilterSearchField";
import { NameIdOption } from "../../../../common/types/commonTypes";
import useTesAutocompleteQuery from "../../../../hooks/useTesAutocompleteQuery";
import {
  fetchTesSalaryTablesByTes,
  TesSalaryTableDocument,
} from "../../../tesSalaryTables/tesSalaryTablesApi";
import {
  BULK_SALARY_FILTER_OPTIONS,
  BULK_SALARY_INCREASE_FILTER_NAMES,
  BULK_SALARY_INCREASE_TARGETS,
  BULK_SALARY_TABLE_FILTER_ALL,
  LABEL_WIDTH,
} from "../constants";
import { chunkArray } from "../helpers";
import { BulkSalaryFilterOptionKeys, SalaryComponentRow } from "../types";
import BulkSalaryIncreaseFilterCell, {
  BulkSalaryIncreaseFilterCellWidths,
  bulkSalaryIncreaseFilterGridWidths,
  BulkSalaryIncreaseFilterRowEmptySlot,
} from "./BulkSalaryIncreaseFilterCell";
import BulkSalaryIncreaseTesTablesTargetFields from "./BulkSalaryIncreaseTesTablesTargetFields";

type Props = {
  getFilter: (
    name: BULK_SALARY_INCREASE_FILTER_NAMES,
  ) => SearchFilter | undefined;
  components: SalaryComponentRow[];
};

const BulkSalaryIncreaseBasicFilters = ({ getFilter, components }: Props) => {
  const { t } = useTranslation();
  const { setTesSearchTerm, tesOptions } = useTesAutocompleteQuery();

  const {
    control,
    getValues,
    watch,
    setValue,
    resetField,
    formState: { errors },
  } = useFormContext();

  const translations = useAppSelector(
    (state) => state.localized.salaryComponent,
  );

  const getLabel = (component: SalaryComponentRow) => {
    return translations[component.code] || component.code;
  };

  const target = watch(BULK_SALARY_INCREASE_FILTER_NAMES.TARGET);
  const isShiftSalariesTarget =
    target === BULK_SALARY_INCREASE_TARGETS.SHIFT_SALARIES;
  const isKtaTarget = target === BULK_SALARY_INCREASE_TARGETS.KTA;
  const isTesTablesTarget = target === BULK_SALARY_INCREASE_TARGETS.TES_TABLES;

  const previousTargetRef = useRef<string | undefined>(undefined);
  const [publishedTables, setPublishedTables] = useState<
    TesSalaryTableDocument[]
  >([]);
  const [loadingTables, setLoadingTables] = useState(false);

  const salaryTypeValue = watch(BULK_SALARY_INCREASE_FILTER_NAMES.SALARY_TYPE);
  const isSalaryTypeMonthly = salaryTypeValue === "MONTHLY";

  useEffect(() => {
    // console.log("Salary type", salaryTypeValue);
    // console.log("Is monthly?", isSalaryTypeMonthly);
    // console.log("Target", target);
    // console.log("Is KTA?", isKtaTarget);
    // console.log("Is shift salaries?", isShiftSalariesTarget);

    /**
     * set some meaningful defaults when target or something like that changes
     */
    if (!isSalaryTypeMonthly) {
      setValue(BULK_SALARY_INCREASE_FILTER_NAMES.WORK_TIME_TYPE, []);
    }
    if (isKtaTarget || isTesTablesTarget) {
      setValue(BULK_SALARY_INCREASE_FILTER_NAMES.BASE_EQUALS_GROUP, "n/a");
      setValue(BULK_SALARY_INCREASE_FILTER_NAMES.SALARY_TYPE, null);
      setValue(BULK_SALARY_INCREASE_FILTER_NAMES.WORK_TIME_TYPE, []);
      setValue(BULK_SALARY_INCREASE_FILTER_NAMES.HAS_CONT_EMP, "n/a");
      setValue(BULK_SALARY_INCREASE_FILTER_NAMES.TES_SALARY_TABLE_ID, "");
      setValue(
        BULK_SALARY_INCREASE_FILTER_NAMES.TES_SELECTION_AT_OR_AFTER_RAISE,
        "n/a",
      );
      setValue(BULK_SALARY_INCREASE_FILTER_NAMES.TES_SELECTION_MODES, []);
    } else {
      setValue(BULK_SALARY_INCREASE_FILTER_NAMES.SALARY_TYPE, "HOURLY");
    }
    // FIXME: fix this or add comment why this is needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    salaryTypeValue,
    isSalaryTypeMonthly,
    setValue,
    target,
    isKtaTarget,
    isTesTablesTarget,
  ]);

  useEffect(() => {
    if (isTesTablesTarget) {
      setValue("tesSalaryTableSourceId", "");
      setValue("tesSalaryTableTargetId", "");
    }
  }, [isTesTablesTarget, setValue]);

  useEffect(() => {
    if (
      target === BULK_SALARY_INCREASE_TARGETS.TES_TABLES &&
      previousTargetRef.current !== BULK_SALARY_INCREASE_TARGETS.TES_TABLES
    ) {
      setValue(BULK_SALARY_INCREASE_FILTER_NAMES.TES, null);
    }
    previousTargetRef.current = target;
  }, [target, setValue]);

  useEffect(() => {
    const tesId = watch(BULK_SALARY_INCREASE_FILTER_NAMES.TES)?.id;
    if (!tesId || isTesTablesTarget || isKtaTarget) {
      setPublishedTables([]);
      setValue(BULK_SALARY_INCREASE_FILTER_NAMES.TES_SALARY_TABLE_ID, "");
      return;
    }
    let cancelled = false;
    setLoadingTables(true);
    fetchTesSalaryTablesByTes(tesId, { status: "PUBLISHED" })
      .then((rows) => {
        if (!cancelled) {
          setPublishedTables(rows);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPublishedTables([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingTables(false);
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    watch(BULK_SALARY_INCREASE_FILTER_NAMES.TES)?.id,
    isTesTablesTarget,
    isKtaTarget,
    setValue,
  ]);

  useEffect(() => {
    if (isShiftSalariesTarget) {
      // setValue(BULK_SALARY_INCREASE_FILTER_NAMES.SALARY_TYPE, "");
      setValue(BULK_SALARY_INCREASE_FILTER_NAMES.WORK_TIME_TYPE, []);
    }
  }, [isShiftSalariesTarget, setValue]);

  const renderOptions = (
    filterName: BulkSalaryFilterOptionKeys,
    multiple: boolean = false,
  ) =>
    BULK_SALARY_FILTER_OPTIONS[filterName].map((option: NameIdOption) => (
      <MenuItem key={option.id} value={option.id}>
        {multiple && (
          <Checkbox checked={getValues(filterName).includes(option.id)} />
        )}
        {t(option.name)}
      </MenuItem>
    ));

  const createGridSelectFilter = (
    filterName: BulkSalaryFilterOptionKeys,
    notRequired: boolean = false,
    disabled: boolean = false,
    renderValue?: () => string,
    multiple: boolean = false,
    itemWidths?: BulkSalaryIncreaseFilterCellWidths,
  ) => {
    const widths = itemWidths ?? bulkSalaryIncreaseFilterGridWidths.fixedThird;
    return (
      <BulkSalaryIncreaseFilterCell
        label={getFilter(filterName)?.displayName}
        itemWidths={widths}
      >
        <Controller
          name={filterName}
          control={control}
          rules={{ required: !notRequired }}
          render={({ field: { value, onChange } }) => (
            <Select
              value={value}
              onChange={onChange}
              disabled={disabled}
              multiple={multiple}
              fullWidth
              size="small"
              renderValue={renderValue}
              sx={{ maxWidth: "230px" }}
              error={!!errors[filterName]}
            >
              {renderOptions(filterName, multiple)}
            </Select>
          )}
        />
      </BulkSalaryIncreaseFilterCell>
    );
  };

  const renderWorkTimeTypeValue = (): string => {
    return getValues(BULK_SALARY_INCREASE_FILTER_NAMES.WORK_TIME_TYPE)
      .map(
        (id: string) =>
          BULK_SALARY_FILTER_OPTIONS[
            BULK_SALARY_INCREASE_FILTER_NAMES.WORK_TIME_TYPE
          ].find((workingTime) => workingTime.id === id)?.name,
      )
      .join(", ");
  };

  const renderSelectionModesValue = (): string => {
    return getValues(BULK_SALARY_INCREASE_FILTER_NAMES.TES_SELECTION_MODES)
      .map(
        (id: string) =>
          BULK_SALARY_FILTER_OPTIONS[
            BULK_SALARY_INCREASE_FILTER_NAMES.TES_SELECTION_MODES
          ].find((mode) => mode.id === id)?.name,
      )
      .join(", ");
  };

  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  return (
    <>
      <Grid
        id="bulk-salary-increase-filters-basic"
        container
        item
        xs={12}
        rowSpacing={1}
        columnSpacing={2}
      >
        {/* target */}
        {createGridSelectFilter(
          BULK_SALARY_INCREASE_FILTER_NAMES.TARGET,
          false,
          false,
          undefined,
          false,
          bulkSalaryIncreaseFilterGridWidths.fixedThird,
        )}
        {/* is increase applied */}
        {createGridSelectFilter(
          BULK_SALARY_INCREASE_FILTER_NAMES.IS_INCREASE_APPLIED,
          false,
          false,
          undefined,
          false,
          bulkSalaryIncreaseFilterGridWidths.fixedThird,
        )}

        {!isKtaTarget && !isTesTablesTarget ? (
          createGridSelectFilter(BULK_SALARY_INCREASE_FILTER_NAMES.HAS_CONT_EMP)
        ) : (
          <BulkSalaryIncreaseFilterRowEmptySlot />
        )}
      </Grid>

      {!isKtaTarget && !isTesTablesTarget && (
        <Grid container item xs={12} rowSpacing={1} columnSpacing={2}>
          {/* salary type */}
          {createGridSelectFilter(
            BULK_SALARY_INCREASE_FILTER_NAMES.SALARY_TYPE,
            isShiftSalariesTarget,
          )}

          {/* work time type */}
          {createGridSelectFilter(
            BULK_SALARY_INCREASE_FILTER_NAMES.WORK_TIME_TYPE,
            !isSalaryTypeMonthly,
            !isSalaryTypeMonthly || isShiftSalariesTarget,
            renderWorkTimeTypeValue,
            true,
          )}

          {/** base salary equals group */}
          {createGridSelectFilter(
            BULK_SALARY_INCREASE_FILTER_NAMES.BASE_EQUALS_GROUP,
            true,
          )}
        </Grid>
      )}

      {!isKtaTarget && !isTesTablesTarget && (
        <Grid container item xs={12} rowSpacing={1} columnSpacing={2}>
          {createGridSelectFilter(
            BULK_SALARY_INCREASE_FILTER_NAMES.TES_SELECTION_AT_OR_AFTER_RAISE,
            true,
          )}
          {createGridSelectFilter(
            BULK_SALARY_INCREASE_FILTER_NAMES.TES_SELECTION_MODES,
            true,
            false,
            renderSelectionModesValue,
            true,
          )}
          <BulkSalaryIncreaseFilterRowEmptySlot />
        </Grid>
      )}

      <Grid container item xs={12} rowSpacing={1} columnSpacing={2}>
        {!isKtaTarget && !isTesTablesTarget && (
          <BulkSalaryIncreaseFilterCell
            label={
              getFilter(BULK_SALARY_INCREASE_FILTER_NAMES.SALARY_DATE_RANGE)
                ?.displayName
            }
            itemWidths={bulkSalaryIncreaseFilterGridWidths.fixedThird}
          >
            <Controller
              name={BULK_SALARY_INCREASE_FILTER_NAMES.SALARY_DATE_RANGE}
              control={control}
              render={({ field: { value, onChange } }) => (
                <DateRangePicker
                  sx={{
                    maxWidth: "230px",
                    // reduce margin between date pickers
                    "& > :not(style) ~ :not(style)": {
                      marginLeft: "8px",
                    },
                  }}
                  format="dd.MM.yyyy"
                  value={value}
                  onChange={onChange}
                  slotProps={{
                    textField: {
                      size: "small",
                      label: null,
                      error:
                        !!errors[
                          BULK_SALARY_INCREASE_FILTER_NAMES.SALARY_DATE_RANGE
                        ],
                    },
                  }}
                />
              )}
            />
          </BulkSalaryIncreaseFilterCell>
        )}

        {isTesTablesTarget ? (
          <BulkSalaryIncreaseTesTablesTargetFields getFilter={getFilter} />
        ) : (
          <>
            <BulkSalaryIncreaseFilterCell
              label={
                getFilter(BULK_SALARY_INCREASE_FILTER_NAMES.TES)?.displayName
              }
              itemWidths={bulkSalaryIncreaseFilterGridWidths.fixedThird}
            >
              <Controller
                name={BULK_SALARY_INCREASE_FILTER_NAMES.TES}
                control={control}
                rules={{ required: true }}
                render={({ field: { value, onChange } }) => (
                  <Autocomplete
                    value={value}
                    options={tesOptions}
                    getOptionLabel={(option) => option?.name || ""}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    onInputChange={(event, newInput) => {
                      if (event?.type === "change") {
                        if (debounceRef.current) {
                          clearTimeout(debounceRef.current);
                        }
                        debounceRef.current = setTimeout(() => {
                          setTesSearchTerm(newInput);
                        }, 500);
                      }
                    }}
                    onChange={(ev, newValue) => {
                      if (newValue !== null) {
                        const vals = getValues();
                        Object.keys(vals)
                          .filter((k) => k.startsWith("_"))
                          .forEach((key) => {
                            resetField(key);
                          });
                      }
                      setValue(
                        BULK_SALARY_INCREASE_FILTER_NAMES.TES_SALARY_TABLE_ID,
                        "",
                      );
                      onChange(newValue);
                    }}
                    renderInput={(params) => (
                      <CommonTextField
                        {...params}
                        sx={{ maxWidth: "230px" }}
                        error={!!errors[BULK_SALARY_INCREASE_FILTER_NAMES.TES]}
                      />
                    )}
                  />
                )}
              />
            </BulkSalaryIncreaseFilterCell>
            <BulkSalaryIncreaseFilterCell
              label={
                getFilter(BULK_SALARY_INCREASE_FILTER_NAMES.TES_SALARY_TABLE_ID)
                  ?.displayName
              }
              itemWidths={bulkSalaryIncreaseFilterGridWidths.fixedThird}
            >
              <Controller
                name={BULK_SALARY_INCREASE_FILTER_NAMES.TES_SALARY_TABLE_ID}
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Select
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value || "")}
                    disabled={
                      !watch(BULK_SALARY_INCREASE_FILTER_NAMES.TES)?.id ||
                      loadingTables
                    }
                    fullWidth
                    displayEmpty
                    size="small"
                    sx={{ maxWidth: "230px" }}
                  >
                    <MenuItem value="">
                      <em>Ei väliä</em>
                    </MenuItem>
                    <MenuItem value={BULK_SALARY_TABLE_FILTER_ALL}>
                      <em>ALL</em>
                    </MenuItem>
                    {publishedTables.map((table) => (
                      <MenuItem
                        key={table.tesSalaryTableId}
                        value={table.tesSalaryTableId}
                      >
                        {`${table.name} (${table.effectiveDate})`}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </BulkSalaryIncreaseFilterCell>
          </>
        )}

        {createGridSelectFilter(
          BULK_SALARY_INCREASE_FILTER_NAMES.IS_UNCLEAR,
          true,
          false,
          undefined,
          false,
          isTesTablesTarget
            ? bulkSalaryIncreaseFilterGridWidths.threePerRowFromMd
            : bulkSalaryIncreaseFilterGridWidths.fixedThird,
        )}
      </Grid>
      {components &&
        components.length > 0 &&
        chunkArray(components, 3).map((chunk) =>
          chunk.map((component: SalaryComponentRow) => {
            return (
              <Grid
                key={component.code}
                container
                item
                xs={4}
                alignItems="center"
                columnSpacing={2}
              >
                <Grid item style={{ width: LABEL_WIDTH, textAlign: "end" }}>
                  <Typography variant="secondary">
                    {getLabel(component)}
                  </Typography>
                </Grid>
                <Grid item xs>
                  <NumberRange
                    name={`_${component.code}`}
                    maxWidth="230px"
                    CustomInputProps={{ unit: "€" }}
                  />
                </Grid>
              </Grid>
            );
          }),
        )}
    </>
  );
};

export default BulkSalaryIncreaseBasicFilters;
